import { Layout } from '@/components/Layout';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useSEO } from '@/lib/useSEO';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { formatDate, statusLabel, statusColor } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import {
  ShoppingCart, Package, MessageSquare, MessageSquareWarning, Truck, ShieldCheck,
  User as UserIcon, Heart, Store, Sparkles, ArrowRight,
} from 'lucide-react';

const TILES = [
  { to: '/mis-pedidos', icon: Package, label: 'Mis pedidos', desc: 'Historial, seguimiento y estado', testid: 'buyer-tile-orders' },
  { to: '/catalogo', icon: ShoppingCart, label: 'Catálogo', desc: 'Explorar repuestos disponibles', testid: 'buyer-tile-catalog' },
  { to: '/mensajes', icon: MessageSquare, label: 'Mensajes', desc: 'Chats con vendedores', testid: 'buyer-tile-messages' },
  { to: '/mis-pqr', icon: MessageSquareWarning, label: 'Mis PQR', desc: 'Peticiones, quejas y reclamos', testid: 'buyer-tile-pqr' },
  { to: '/envios', icon: Truck, label: 'Envíos', desc: 'Políticas y tiempos', testid: 'buyer-tile-shipping' },
  { to: '/garantias', icon: ShieldCheck, label: 'Garantías', desc: 'Condiciones y proceso', testid: 'buyer-tile-warranty' },
  { to: '/favoritos', icon: Heart, label: 'Favoritos', desc: 'Productos guardados', testid: 'buyer-tile-favorites' },
  { to: '/lista-de-deseos', icon: Sparkles, label: 'Lista de deseos', desc: 'Repuestos que quiero conseguir', testid: 'buyer-tile-wishlist' },
  { to: '/vendedores-que-sigo', icon: Store, label: 'Vendedores que sigo', desc: 'Tiendas favoritas', testid: 'buyer-tile-follows' },
  { to: '/perfil', icon: UserIcon, label: 'Perfil', desc: 'Datos personales y contraseña', testid: 'buyer-tile-profile' },
];

export default function BuyerDashboard() {
  const { user } = useAuth();
  const [recentOrders, setRecentOrders] = useState([]);
  useSEO({
    title: 'Panel del comprador | AutoMarket Colombia',
    description: 'Accesos rápidos a pedidos, mensajes, PQR, favoritos y más.',
    canonical: '/comprador',
  });

  // FASE B — Últimas 3 órdenes activas. Excluye entregadas/canceladas para
  // que el vistazo rápido enfoque en lo que aún requiere atención (paid,
  // shipped). Si no hay, cae en las 3 más recientes independientemente.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/orders/me', { params: { limit: 3, active_only: true } });
        if (!cancelled) setRecentOrders(data.items || []);
      } catch {
        if (!cancelled) setRecentOrders([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <Layout>
      <section className="border-b border-slate-200 bg-slate-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-400">Panel del comprador</div>
          <h1 className="font-display text-3xl font-black tracking-tighter mt-1" data-testid="buyer-dashboard-title">
            Hola, {user?.name?.split(' ')[0] || 'comprador'}
          </h1>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {/* Últimas 3 órdenes activas */}
        {recentOrders.length > 0 && (
          <section className="mb-10" data-testid="buyer-dashboard-recent-orders">
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Últimas órdenes</div>
                <h2 className="font-display text-xl font-black tracking-tighter text-slate-900 mt-1">Actividad reciente</h2>
              </div>
              <Link
                to="/mis-pedidos"
                data-testid="buyer-dashboard-see-all-orders"
                className="text-xs font-semibold text-blue-700 hover:underline inline-flex items-center gap-1"
              >
                Ver todos <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {recentOrders.map((o) => {
                const firstItem = (o.items || [])[0];
                return (
                  <Link
                    key={o.order_id}
                    to={`/mis-pedidos?open=${o.order_id}`}
                    data-testid={`buyer-dashboard-order-${o.order_id}`}
                    className="border border-slate-200 bg-white p-4 hover:border-slate-900 hover:shadow-sm transition-all block"
                  >
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-mono text-slate-500">{o.order_id}</span>
                      <Badge className={`rounded-none border ${statusColor[o.status] || ''}`}>
                        {statusLabel[o.status] || o.status}
                      </Badge>
                    </div>
                    <div className="mt-2 text-sm font-semibold text-slate-900 line-clamp-2">
                      {firstItem?.name || 'Pedido'}
                      {o.items?.length > 1 && (
                        <span className="text-slate-500 font-normal"> +{o.items.length - 1} más</span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      Vendedor: {o.seller_name || firstItem?.seller_name || '—'}
                    </div>
                    {o.tracking_code && (
                      <div className="mt-2 text-[11px] text-slate-500">
                        <span className="font-bold uppercase tracking-wider mr-1">Tracking:</span>
                        <span className="font-mono">{o.tracking_code}</span>
                      </div>
                    )}
                    <div className="mt-2 text-[11px] text-slate-400">{formatDate(o.created_at)}</div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="buyer-dashboard-tiles">
          {TILES.map((t) => (
            <Link
              key={t.to}
              to={t.to}
              data-testid={t.testid}
              className="border border-slate-200 bg-white p-5 hover:border-slate-900 hover:shadow-sm transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center bg-slate-900 text-white">
                  <t.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{t.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{t.desc}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </Layout>
  );
}
